import { useEffect, useRef, useState } from "react";

/**
 * Animated number counter that triggers when scrolled into view.
 * Parses the leading numeric portion of `value` (e.g. "12+", "50%", "1,200 m²")
 * and animates it from 0 to that number, preserving any suffix.
 */
export default function AnimatedCounter({ value, duration = 1600, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const str = String(value || "");
    const match = str.match(/^(-?[\d\s.,]+)(.*)$/);
    if (!match) {
      setDisplay(str);
      return;
    }
    const numericPart = match[1].replace(/[\s,]/g, "");
    const suffix = match[2] || "";
    const target = parseFloat(numericPart);
    if (!isFinite(target)) {
      setDisplay(str);
      return;
    }
    const decimals = (numericPart.split(".")[1] || "").length;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const current = target * eased;
              const formatted = decimals > 0 ? current.toFixed(decimals) : Math.round(current).toString();
              setDisplay(formatted + suffix);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
